import { useCallback, type ReactNode } from "react";
import { Box, chakra, Flex } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Icon } from "../../components/Icon/Icon";

const MChakraDiv = motion.create(chakra.div);

const modalVariants = {
  initial: { opacity: 0, scale: 0.92 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.2, ease: "easeOut" as const },
  },
  exit: {
    opacity: 0,
    scale: 0.96,
    transition: { duration: 0.15, ease: "easeIn" as const },
  },
};

// React Router v6 stamps an incrementing `idx` into history.state on every
// push. idx === 0 means this is the first entry in the session (deep link,
// refresh, external referrer), so there's nothing safe to pop back to.
export function useModalBack(fallback: string) {
  const navigate = useNavigate();
  return useCallback(() => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate(fallback, { replace: true });
  }, [navigate, fallback]);
}

export function ModalPage({
  motionKey,
  title,
  fallback,
  rightAction,
  stickyBelowHeader,
  animateEntrance = true,
  bodyBg = "bg.subtle",
  children,
  header,
}: {
  motionKey: string;
  title?: ReactNode;
  fallback: string;
  rightAction?: ReactNode;
  stickyBelowHeader?: ReactNode;
  animateEntrance?: boolean;
  bodyBg?: string;
  children: ReactNode;
  // Escape hatch for pages that render above the header (e.g. <Meta />).
  header?: ReactNode;
}) {
  const onBack = useModalBack(fallback);

  return (
    <MChakraDiv
      key={motionKey}
      initial={animateEntrance ? "initial" : false}
      animate="animate"
      exit="exit"
      variants={modalVariants}
      style={{
        position: "fixed",
        inset: 0,
        background: "white",
        zIndex: 30,
        display: "flex",
        flexDirection: "column",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
        transformOrigin: "center",
      }}
    >
      {header}
      <Flex direction="column" flex="1" minH="0" bg="bg.canvas">
        <Box
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          h="56px"
          px="4"
          bg="bg.canvas"
          zIndex="2"
          flexShrink={0}
        >
          <chakra.button
            type="button"
            onClick={onBack}
            aria-label="Back"
            bg="transparent"
            border="none"
            cursor="pointer"
            color="fg.default"
            display="flex"
            alignItems="center"
            gap="1"
            fontSize="sm"
            fontWeight="semibold"
            p="2"
            ml="-2"
          >
            <Icon name="chevron-left" boxSize="14px" />
            BACK
          </chakra.button>
          {title !== undefined && (
            <chakra.h1
              textStyle="bodyLg"
              fontWeight="semibold"
              m="0"
              position="absolute"
              left="50%"
              top="50%"
              transform="translate(-50%, -50%)"
              truncate
              maxW="60%"
            >
              {title}
            </chakra.h1>
          )}
          <Box minW="60px" display="flex" justifyContent="flex-end">
            {rightAction}
          </Box>
        </Box>
        {stickyBelowHeader && <Box flexShrink={0}>{stickyBelowHeader}</Box>}
        <Box
          flex="1"
          minH="0"
          overflowY="auto"
          // Clip horizontal overflow so transient off-screen content (e.g.
          // the Match profile swiping out to the right) doesn't flash a
          // horizontal scrollbar mid-animation.
          overflowX="hidden"
          // Reserve scrollbar space so content doesn't shift horizontally
          // when height changes across renders (e.g. swiping through Match
          // profiles where the next card has fewer extra photos).
          css={{ scrollbarGutter: "stable" }}
          display="flex"
          flexDirection="column"
          position="relative"
          bg={bodyBg}
        >
          {children}
        </Box>
      </Flex>
    </MChakraDiv>
  );
}
